'use client';

import NextImage from 'next/image';
import Script from 'next/script';
import { ReactNode, useState } from 'react';
import { env } from 'next-runtime-env';
import { useCookieBannerHeight } from 'lib/hooks';
import { cn } from 'lib/utils';

type DecagonEvent = { eventName: string };

declare global {
  interface Window {
    duet?: {
      addMetadata: (metadata: Record<string, string>) => void;
      hide: () => void;
      open: () => void;
      setEventListener: (callback: (event: DecagonEvent) => void) => void;
      updateStyle: (style: Partial<CSSStyleDeclaration>) => void;
    };
  }
}

const DECAGON_LOADER_SRC = 'https://decagon.ai/loaders/release/base/v1.js';

const DECAGON_SLUG = '3prpwP9_oura_b2b_sales';

// One above OneTrust's banner, so a chat opened before a consent choice isn't covered by it.
const CHAT_PANEL_Z_INDEX = '2147483646';

const LAUNCHER_INSET_PX = 20;

export const DecagonChat = (): ReactNode => {
  const [requested, setRequested] = useState(false);
  const [ready, setReady] = useState(false);
  const environment = env('NEXT_PUBLIC_OURA_ENV');
  const cookieBannerHeight = useCookieBannerHeight();
  const loading = requested && !ready;

  const openChat = () => {
    setRequested(true);
    window.duet?.open();
  };

  const initializeChat = () => {
    window.duet?.setEventListener(({ eventName }) => {
      if (eventName !== 'DECAGON_LOADED') return;
      window.duet?.updateStyle({ zIndex: CHAT_PANEL_Z_INDEX });
      setReady(true);
    });
    window.duet?.hide();
    if (environment) {
      window.duet?.addMetadata({ environment });
    }
    window.duet?.open();
  };

  return (
    <>
      <button
        type="button"
        aria-label="Open chat"
        aria-busy={loading}
        onClick={openChat}
        style={{ bottom: cookieBannerHeight + LAUNCHER_INSET_PX }}
        className={cn(
          'fixed right-5 z-[99999999] flex size-16 cursor-pointer items-center justify-center rounded-full bg-clay-50 shadow-lg transition-[bottom,scale] duration-300 hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500',
          loading && 'animate-pulse cursor-wait'
        )}
      >
        <NextImage
          src="/_assets/decagon-chat-avatar-20261001.png"
          alt=""
          width={58}
          height={58}
        />
      </button>

      {requested && (
        // afterInteractive is required: next/script only forwards data-* on the imperative strategies.
        <Script
          id="decagon-chat-loader"
          src={DECAGON_LOADER_SRC}
          data-slug={DECAGON_SLUG}
          strategy="afterInteractive"
          onLoad={initializeChat}
        />
      )}
    </>
  );
};
